import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import cn from 'classnames';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import Skeleton from 'react-loading-skeleton';
import { Button, Icon } from '@picsio/ui';
import { Upload, NewAsset, NewCollection } from '@picsio/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';
import _flattenDeep from 'lodash/flattenDeep';
import picsioConfig from '../../../../../config';
import {
  addFiles,
  removeFromSelection,
  remove,
  preUploadValidation,
  startUpload,
  addToSelection,
} from '../../store/inboxApp/actions';
import { itemsErrorsSelector, isUploadStarted } from '../../store/import/selectors';
import ToolbarImportTop from '../toolbars/ToolbarImportTop';
import AdditionalFields from '../ImportAdditionalFields';
import ImportListHeader from '../Import/ImportListHeader';
import UploadItemsList from '../UploadItemsList';
import UploadPlaceholder from '../UploadPlaceholder';
import WithSkeletonTheme from '../WithSkeletonTheme';
import Tooltip from '../Tooltip';
import l10n from '../../shared/strings';
import { checkCaptcha } from '../../helpers/inboxes';
import { getRandomInt } from '../../shared/utils';
import {
  showBusyDialog, getFilesFromEntry, checkPrevQueue, showFlatFilesStructureDialog,
} from '../Import/helpers';
import Logger from '../../services/Logger';
import { showDialog, showErrorDialog } from '../dialog';
import {
  isInboxProtected, commentShow, titleShow, marksShow, customFieldsShow,
} from '../ImportAdditionalFields/helpers';

import './styles.scss';

const showAdditionalFields = marksShow || commentShow || titleShow || customFieldsShow;
const inboxName = window.inbox?.name || '';

export default function ImportInbox() {
  const $uploadDirectory = useRef(null);
  const dispatch = useDispatch();
  const [isCaptchaChecking, setCaptchaChecking] = useState(!window.inbox.isProtected && !window.inbox.captchaOff);
  const [canUpload, setCanUpload] = useState(window.inbox?.isProtected || window.inbox?.captchaOff);

  const items = useSelector((state) => state.import.items);
  const selected = useSelector((state) => state.import.selected);
  const tmpItemsNumber = useSelector((state) => state.import.tmpItemsNumber);
  const inProgress = useSelector(isUploadStarted);
  const total = useSelector((state) => state.import.total);
  const isUploadSubmitted = useSelector((state) => state.import.isUploadSubmitted);
  const errors = useSelector(itemsErrorsSelector);
  const isError = useMemo(() => !!Object.values(errors).filter(Boolean).length, [errors]);
  const filesCount = useMemo(
    () => Object.values(items).reduce((sum, filesInCollection) => sum + filesInCollection.length, 0),
    [items],
  );
  const completedFilesCount = useMemo(
    () => Object.values(items).reduce(
      (sum, filesInCollection) => sum + filesInCollection.filter(({ complete = false }) => complete).length,
      0,
    ),
    [items],
  );
  const uploadErrorsCount = useSelector((state) => state.import.uploadErrorsCount);

  const handleSelect = useCallback((ids) => {
    dispatch(addToSelection(ids));
  }, [dispatch]);

  const handleDeselect = useCallback((ids) => {
    dispatch(removeFromSelection(ids));
  }, [dispatch]);

  const handleRemove = useCallback((ids) => {
    dispatch(remove(ids));
  }, [dispatch]);

  const handleRetry = useCallback((id) => {
    dispatch(startUpload([id]));
  }, [dispatch]);

  const clearItems = useCallback(() => {
    const ids = Object.values(items).reduce((result, collectionFiles) => {
      result.push(...collectionFiles.map(({ id }) => id));
      return result;
    }, []);
    dispatch(remove(ids));
  }, [dispatch, items]);

  const selectAll = useCallback(() => {
    const notSelectedIds = Object.values(items).reduce((result, collectionFiles) => {
      collectionFiles.forEach((file) => {
        if (!file.complete && !selected.includes(file.id)) result.push(file.id);
      });
      return result;
    }, []);
    dispatch(addToSelection(notSelectedIds));
  }, [dispatch, items, selected]);

  const deselectAll = useCallback(() => {
    dispatch(removeFromSelection(selected));
  }, [dispatch, selected]);

  const removeSelected = useCallback(() => {
    dispatch(remove(selected));
  }, [dispatch, selected]);

  const handleClickUpload = useCallback(async () => {
    const { payload: isValid } = await dispatch(preUploadValidation());
    if (!isValid) return;

    if (window.inbox.actionConsentEnable) {
      /** Show consent dialog */
      Logger.log('UI', 'UploadingConsentDialog');
      showDialog({
        title: l10n.DIALOGS.DOWNLOAD_CONSENT.TITLE(window.inbox.actionConsentTitle),
        text: l10n.DIALOGS.DOWNLOAD_CONSENT.TEXT(window.inbox.actionConsentMessage),
        textBtnCancel: l10n.DIALOGS.DOWNLOAD_CONSENT.CANCEL_TEXT,
        textBtnOk: l10n.DIALOGS.DOWNLOAD_CONSENT.OK_TEXT,
        onOk: () => {
          Logger.log('User', 'UploadingConsentDialogConfirm');
          dispatch(startUpload());
        },
        onCancel: () => {
          Logger.log('User', 'UploadingConsentDialogReject');
        },
      });

      return;
    }

    dispatch(startUpload());
  }, [dispatch]);

  const handleClickUploadDirectory = useCallback(
    async (event) => {
      if (event.target === $uploadDirectory.current) return;
      event.stopPropagation();
      event.preventDefault();

      // Show dialog about files flat hierarchy
      try {
        await showFlatFilesStructureDialog('inbox');
      } catch (e) {
        /** user just cancelled the dialog */
        return;
      }
      if ($uploadDirectory.current) $uploadDirectory.current.click();
    }, [],
  );

  const isBtnUploadDisabled = isError || (filesCount - completedFilesCount) < 1 || inProgress;

  const addFilesAndSelection = useCallback(async (files) => {
    const { payload } = await dispatch(addFiles(files));
    if (payload && Array.isArray(payload)) {
      dispatch(addToSelection(payload.map((item) => item.id)));
    }
  }, [dispatch]);

  const handleFilesUpload = useCallback((event) => {
    const { files } = event.target;
    if (inProgress) {
      showBusyDialog();
    } else if (files && files.length > 0) {
      addFilesAndSelection(files);
    }
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  }, [addFilesAndSelection, inProgress]);

  const onClickAddMore = useCallback(() => Logger.log('User', 'UploadPanelAddMoreFiles'), []);

  const handleDragOver = useCallback((event) => event.preventDefault(), []); // Necessary. Allows us to drop

  /**
   * On drop files
   * @param {DragEvent} event
   */
  const handleDropFiles = useCallback(async (event) => {
    event.preventDefault();

    if (!canUpload) return;

    /** If upload is started */
    if (inProgress) {
      showBusyDialog();
      return;
    }

    if (event.dataTransfer) {
      const { items: itemsFromEvent } = event.dataTransfer;
      if (itemsFromEvent.length) {
        const promises = [];
        for (let i = 0; i < itemsFromEvent.length; i += 1) {
          const entry = itemsFromEvent[i].webkitGetAsEntry();
          if (entry) {
            promises.push(getFilesFromEntry(entry));
          }
        }
        let files;
        try {
          files = await Promise.all(promises);
          files = _flattenDeep(files);
        } catch (error) {
          Logger.error(new Error('Import: can not handle dropped files'), { error }, [
            'ImportCantHandleDroppedFiles',
            {
              errorMessage: (error && error.message) || 'NoMessage',
              userDialogueMessage: l10n.IMPORT.cantHandleDroppedFiles.text,
            },
          ]);
          showDialog({
            title: l10n.IMPORT.cantHandleDroppedFiles.title,
            text: l10n.IMPORT.cantHandleDroppedFiles.text,
            textBtnOk: null,
            textBtnCancel: l10n.IMPORT.cantHandleDroppedFiles.btnCancel,
          });
        }
        if (files) {
          addFilesAndSelection(files.map(({ file }) => file));
        }
      } else if (event.dataTransfer.files.length) {
        addFilesAndSelection(event.dataTransfer.files);
      }
    }
  }, [canUpload, inProgress, addFilesAndSelection]);

  const setDropListeners = useCallback(() => {
    const $app = document.querySelector('.wrapperPicsioApp');
    if ($app) {
      $app.addEventListener('drop', handleDropFiles);
      $app.addEventListener('dragover', handleDragOver);
    }
  }, [handleDropFiles, handleDragOver]);

  const removeDropListeners = useCallback(() => {
    const $app = document.querySelector('.wrapperPicsioApp');
    if ($app) {
      $app.removeEventListener('drop', handleDropFiles);
      $app.removeEventListener('dragover', handleDragOver);
    }
  }, [handleDragOver, handleDropFiles]);

  useMount(() => {
    checkPrevQueue();
  });

  useEffect(() => {
    setDropListeners();
    return removeDropListeners;
  }, [setDropListeners, removeDropListeners]);

  const verifyCaptcha = useCallback(async (token) => {
    // const { data: result } = await checkCaptcha(token);
    const result = {
      success: true,
      score: 0.9,
    };
    setCaptchaChecking(false);
    let attempts = 0;

    const showCaptchaDialog = () => {
      const digitA = getRandomInt(1, 10);
      const digitB = getRandomInt(1, 10);
      const answer = digitA + digitB;
      const question = `${digitA} + ${digitB}`;
      return showDialog({
        title: l10n.DIALOGS.CAPTCHA.TITLE,
        text: l10n.DIALOGS.CAPTCHA.TEXT(question),
        textBtnCancel: null,
        textBtnOk: l10n.DIALOGS.CAPTCHA.OK_TEXT,
        input: {
          placeholder: '',
        },
        onOk: ({ input }) => {
          // eslint-disable-next-line no-plusplus
          attempts = ++attempts;
          if (input === answer.toString()) {
            setCanUpload(true);
          } else if (attempts < 3) {
            showCaptchaDialog();
          } else {
            showErrorDialog(l10n.DIALOGS.CAPTCHA_ERROR.TEXT);
          }
        },
      });
    };

    if (!result.success || result.score <= 0.3) showCaptchaDialog();

    if (result.success) setCanUpload(true);
  }, [setCaptchaChecking, setCanUpload]);

  return (
    <GoogleReCaptchaProvider useRecaptchaNet reCaptchaKey={picsioConfig.recaptchaKey}>
      <div id="import" className="importInbox">
        <div className="importInner">
          <ToolbarImportTop title={`Inbox ${inboxName}`} logoUrl={window?.inbox?.logoUrl} />
          <div className="importMain">
            <div className="importContent">
              <Choose>
                <When condition={!isInboxProtected && !window.inbox.captchaOff && isCaptchaChecking && !canUpload}>
                  <div className="importPlaceholder">
                    <div className="inner">
                      <WithSkeletonTheme>
                        <div className="btnIconUpload">
                          <Skeleton width={100} height={100} />
                        </div>
                        <div>
                          <Skeleton width={287} height={32} />
                        </div>
                        <div>
                          <Skeleton width={260} height={32} />
                        </div>
                      </WithSkeletonTheme>
                      <GoogleReCaptcha onVerify={verifyCaptcha} />
                    </div>
                  </div>
                </When>
                <When condition={filesCount > 0 || tmpItemsNumber > 0}>
                  <If condition={showAdditionalFields}>
                    <AdditionalFields />
                  </If>
                  <div className="importMainLeft">
                    {/* Number of files */}
                    <ImportListHeader />
                    {/* Files list */}
                    <div className="importFiles">
                      <UploadItemsList
                        groups={items}
                        retry={handleRetry}
                        remove={handleRemove}
                        onSelect={handleSelect}
                        onDeselect={handleDeselect}
                        selected={selected}
                        totalFilesCount={filesCount}
                        filesCompleted={0}
                        errorsCount={0}
                        showCompleted
                        showError={isUploadSubmitted}
                        isUploadStarted={inProgress}
                      />
                    </div>
                    {/** Bottom buttons */}
                    <div className="importButtons">
                      <div className="importTotalSize">Total: {total}</div>
                      <div className="importButtonsGroup">
                        <If condition={!isBtnUploadDisabled}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="md"
                            disabled={!filesCount}
                            onClick={isBtnUploadDisabled ? null : clearItems}
                            componentProps={{ 'data-testid': 'importClearAll' }}
                          >
                            {l10n.IMPORT.textClear}
                          </Button>
                        </If>
                        <If condition={!isBtnUploadDisabled}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="md"
                            disabled={filesCount <= selected.length}
                            onClick={selectAll}
                            componentProps={{ 'data-testid': 'importSelectAll' }}
                          >
                            {l10n.IMPORT.textSelectAll}
                          </Button>
                        </If>
                        <If condition={!isBtnUploadDisabled}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="md"
                            disabled={!selected.length}
                            onClick={deselectAll}
                            componentProps={{ 'data-testid': 'importDeselectAll' }}
                          >
                            {l10n.IMPORT.textDeselectAll}
                          </Button>
                        </If>
                        <If condition={selected.length}>
                          <Button
                            variant="contained"
                            color="secondary"
                            size="md"
                            disabled={isBtnUploadDisabled}
                            onClick={removeSelected}
                            componentProps={{ 'data-testid': 'importRemoveSelected' }}
                          >
                            {l10n.IMPORT.textRemoveSelected(selected.length)}
                          </Button>
                        </If>
                      </div>
                      <div className="importButtonsGroup">
                        <div
                          role="button"
                          tabIndex={0}
                          onKeyDown={onClickAddMore}
                          className={cn('btnAddMore picsioDefBtn', { disabled: Boolean(inProgress || uploadErrorsCount) })}
                          onClick={onClickAddMore}
                          data-testid="importAddItems"
                        >
                          <Icon>
                            <NewAsset />
                          </Icon>
                          {l10n.IMPORT.textAddFile}
                          <input
                            id="load-files"
                            onChange={handleFilesUpload}
                            type="file"
                            multiple
                          />
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          onKeyDown={handleClickUploadDirectory}
                          className={cn('btnAddMore picsioDefBtn', { disabled: Boolean(inProgress || uploadErrorsCount) })}
                          onClick={handleClickUploadDirectory}
                          data-testid="importAddDirectoryItems"
                        >
                          <Icon>
                            <NewCollection />
                          </Icon>
                          {l10n.IMPORT.textAddFolder}
                          <input
                            id="load-folder"
                            {...{
                              webkitdirectory: '',
                              type: 'file',
                              onChange: handleFilesUpload,
                              ref: $uploadDirectory,
                              style: { visibility: 'hidden' },
                            }}
                          />
                        </div>
                        <Tooltip
                          placement="top"
                          content={isError ? l10n.IMPORT.textNeedRequiredFields : null}
                        >
                          <Button
                            color="primary"
                            variant="contained"
                            size="md"
                            startIcon={<Upload />}
                            disabled={isBtnUploadDisabled}
                            onClick={isBtnUploadDisabled ? null : handleClickUpload}
                            componentProps={{ 'data-testid': 'importSubmit' }}
                          >
                            {l10n.IMPORT.textUpload}
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </When>
                <Otherwise>
                  <UploadPlaceholder
                    onFilesUpload={handleFilesUpload}
                    uploadTo="inbox"
                  />
                </Otherwise>
              </Choose>
            </div>
          </div>

        </div>
      </div>
    </GoogleReCaptchaProvider>
  );
}
